import { useCallback, useContext, useEffect } from 'react'
import { parseAsString, useQueryState } from 'nuqs'

import { fareClassToUrlMap, parseFareClassFromUrl, type FareClassWithReward } from 'constants/flight'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useCharterFareClassPolicy from 'hooks/Flights/useCharterFareClassPolicy'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import MaldivesCharterFlightFareClassModal from 'components/OfferPage/Filters/CharterFlight/MaldivesCharterFlightFareClassModal'

interface Params {
  offer?: App.Offer
  fareClass: FareClassWithReward
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
}

interface Result {
  canOpen: boolean
  open: () => Promise<void>
}

export default function useOpenCharterFareClassModal({
  offer,
  fareClass,
  checkInDate,
  checkOutDate,
  occupancies,
}: Params): Result {
  const openModal = useContext(ModalContext)
  const dispatch = useOfferPageDispatch()
  const [fareClassUrlRaw, setFareClassUrl] = useQueryState('fareClass', parseAsString)
  const fareClassFromUrl = fareClassUrlRaw ? parseFareClassFromUrl(fareClassUrlRaw) : undefined

  useEffect(() => {
    if (fareClassFromUrl && fareClassFromUrl !== fareClass) {
      dispatch({ type: OfferPageActions.setFareClass, fareClass: fareClassFromUrl })
    }
  }, [fareClassFromUrl, fareClass, dispatch])

  const charterFareClassPolicy = useCharterFareClassPolicy(offer, occupancies)
  const allowedFareClassesForModal = useMaldivesCharterModalFareClasses(charterFareClassPolicy.allowedFareClasses)
  const canOpen = allowedFareClassesForModal.length > 1

  const open = useCallback(async() => {
    const selectedClass = await openModal<FareClassWithReward>(
      <MaldivesCharterFlightFareClassModal
        selectedClassId={fareClass}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        occupancies={occupancies}
        allowedFareClasses={allowedFareClassesForModal}
      />,
    )
    if (selectedClass) {
      dispatch({ type: OfferPageActions.setFareClass, fareClass: selectedClass })
      setFareClassUrl(fareClassToUrlMap[selectedClass])
    }
  }, [openModal, fareClass, checkInDate, checkOutDate, occupancies, allowedFareClassesForModal, dispatch, setFareClassUrl])

  return { canOpen, open }
}
